/**
 * ⭐⭐ **THE IN-APP FORM FIELD — the treatment this package did not have, and the reason it did not.**
 *
 * # The gap, measured 2026-08-07
 *
 * Before this file the package owned exactly **two** field treatments, and neither could be used by a
 * create form:
 *
 * | treatment | where | why it does not fit a create form |
 * |---|---|---|
 * | `FIELD_CLASS` | `FilterSection.tsx` | ⛔ **not exported at the time** (it is exported now), and it is the DENSE filter-bar box (`py-1 text-xs rounded`) — a row of controls scanned at a glance |
 * | `AUTH_FIELD_CLASS` | `AuthCardFrame.tsx` | exported, but pre-auth only: `AuthField.type` is a closed `'email' \| 'password'` union, deliberately |
 *
 * Everything else was hand-rolled per site — `PeoplePane`, `TenantPane`, `AppLogsPane`,
 * `AppFixturesPane`, `change-role-dialog`. So *"a page never hand-rolls what the library owns"* had a
 * hole exactly where a form goes. The importers today are the channel forms and the second-factor
 * sign-in pages.
 *
 * # ⛔ WHY THIS IS NOT A COPY OF `AUTH_FIELD_CLASS`, AND WHY IT MUST NOT BECOME ONE
 *
 * Two independent reasons, and the second is the interesting one:
 *
 * 1. **The rule in `AuthCardFrame`'s own docblock decides the background, and it decides it the other
 *    way here.** *"A field must not be lower-contrast than the chrome it sits in, or the control reads
 *    as a label."* Pre-auth sits on an ELEVATED card ⇒ its field is `bg-surface`. A muted in-app panel
 *    (`bg-surface-muted`) ⇒ its field is `bg-surface-elevated`. **Two treatments, one rule** — in that
 *    file's words. Sharing one string would make one of the two surfaces wrong.
 * 2. ⛔ **`pre-auth-adoption.test.ts` asserts `occurrencesOf(AUTH_FIELD_CLASS) === 1` over ALL of
 *    `src/**` production code.** A byte-identical copy anywhere reds it. ⚠️ That gate is right and the
 *    temptation to "just reuse the string" is what it exists to stop — but note the ordering: **the
 *    design reason above is why these differ, and the gate is only what makes it stick.** A difference
 *    introduced to dodge a gate would be a different and much worse thing.
 *
 * ✅ What IS shared is the *generosity*: `rounded-lg px-3 py-2 text-sm`, not the filter bar's
 * `rounded px-2 py-1 text-xs`. Same reason `AuthCardFrame` gives — a filter is scanned, a form field is
 * the thing the operator is typing into, and `Button`'s `'sm'` is the in-row size for the same reason.
 *
 * # ⚠️ WHAT THIS FILE DELIBERATELY DOES NOT ADD
 *
 * ⛔ **No select, no checkbox, no search box, no multi-select.** The WARD controls own them:
 * `FilterSelect` and `SearchField` in `src/ward/`, and `Checkbox` from `@witness-ai/ward-design-system`.
 * `primitive-adoption.test.ts` enforces an EXACT SET
 * of hand-rolled checkboxes in `src/panes`. Plan 2026-10-22-console-cleanup-g3 deleted the shell
 * filter controls that this paragraph once named, because no page used them.
 *
 * # ⛔⛔ THERE IS NO SECRET MASK IN THIS MODULE, AND ITS ABSENCE IS THE DECISION
 *
 * See {@link FormFieldControl}'s `secret` arm. `form-field.test.tsx` asserts the absence over this
 * file's own source text, with a positive control on the scanner — because *"we did not write one"* is
 * the kind of claim that decays the first time somebody adds a convenience default.
 */
import type { ChangeEvent, KeyboardEvent } from 'react';
import { dataToneClass } from './data-tone';

/**
 * ⛔ **ONE string literal, not a `+` concatenation across two lines.** Several gates in this package
 * resolve `const X = '…'` with a single-literal regex and see only the first half of a concatenation —
 * a treatment half-visible to a text scan is worse than one fully visible or fully hidden, because the
 * scan reports a confident answer about a fragment. (The same note is on `AUTH_FIELD_CLASS`.)
 */
export const FORM_FIELD_CLASS =
  'rounded-lg border border-border-subtle bg-surface-elevated px-3 py-2 text-sm text-primary placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

/**
 * The label treatment, matching `AuthField`'s — ⛔ sentence case, NOT the filter bar's `MetaLabel`.
 *
 * ⚠️ **This governs `FormField`'s OWN label and nothing else** (spine **R-53**, which corrected a
 * ruling built on the opposite view). It is NOT a prohibition on a `<MetaLabel>` legend for a group
 * of controls. Whether the console wants a form-legend variant console-wide is filed as
 * design-language debt (**R-53(C)**), deliberately NOT decided by this constant.
 */
export const FORM_LABEL_CLASS = 'flex flex-col gap-1 text-xs text-secondary';

/**
 * ⛔ A closed vocabulary, `as const` + a derived type — this package's house style — so a gate can
 * **iterate the arms** rather than trusting a `switch` to have covered them.
 */
export const FORM_FIELD_CONTROL_KINDS = ['text', 'url', 'multiline', 'numeric', 'secret'] as const;
export type FormFieldControlKind = (typeof FORM_FIELD_CONTROL_KINDS)[number];

/**
 * Which control a {@link FormField} renders.
 *
 * ## ⛔⛔ A DISCRIMINATED UNION, NOT A `kind` STRING BESIDE SIBLING OPTIONAL PROPS
 *
 * Two authors proposed the two obvious alternatives and both make a meaningless state representable:
 * `kind: 'text'` beside a sibling `rows?: number` permits `{kind:'text', rows:5}`; two sibling objects
 * `multiline?` and `numeric?` permit both at once. This shape makes `rows` on a text field, `min`/`max`
 * on a textarea, and multiline-and-numeric-together **unrepresentable** rather than merely wrong.
 *
 * ## ⛔ AND IT HONOURS THE NO-`type`-PROP RULING RATHER THAN DODGING IT
 *
 * {@link FormField} refuses a forwarded `type` because *"a `type` this component forwarded blindly is
 * how a credential field ends up as `type='text'`"*. A closed arm cannot express an arbitrary `type`,
 * so no caller can reach one — a **stronger** guarantee than the string it replaces, not a weaker one.
 */
export type FormFieldControl =
  | { readonly kind: 'text' }
  /**
   * ⛔⛔ **A CLOSED ARM, NOT A FORWARDED `type`** — the same shape the `secret` arm below uses, and
   * for a related reason. The first adopter is the alert-destination create form, whose value is a
   * Slack incoming-webhook URL, i.e. a bearer credential. It needs three attributes together:
   * `type="url"`, `autoComplete="off"` and `spellCheck={false}`.
   *
   * ⛔ **`autoComplete="off"` is why this is an ARM and not a `type` prop.** {@link FormFieldProps.name}
   * is required, and a named credential-bearing field with no `autoComplete` is a browser autofill
   * AND save target — the hazard the `secret` arm's R-56 note is about. A caller-supplied `type`
   * would carry the `type` and silently leave the other two behind, so the three travel together
   * here, chosen by this file, where no call site can drop one of them.
   *
   * ⚠️ `'off'` is the right token on THIS arm, unlike the `secret` arm: Chrome ignores
   * `autocomplete="off"` on password fields only.
   *
   * ⛔ It carries no payload. `type="url"` is a keyboard and validation hint, not a claim about the
   * value — the value is still a string this module never parses.
   */
  | { readonly kind: 'url' }
  | { readonly kind: 'multiline'; readonly rows: number }
  | { readonly kind: 'numeric'; readonly min: number; readonly max: number }
  /**
   * ⛔⛔ **NO PAYLOAD, AND THE ABSENCE IS THE DESIGN** (spine **R-51 as updated**, adopting 06a's D-17).
   *
   * The first cut of this arm carried `unchangedSentinel: string` — the producer's mask, echoed into
   * the field, meaning *leave unchanged*. **That was overruled and the replacement is safer.** With a
   * sentinel in the field, *"the operator submitted without touching it"* and *"the operator retyped
   * the mask"* are **the same bytes**, so persisting the literal mask **as the credential** is a live
   * failure defended only by a test. With an empty field it is **unrepresentable** — there is no mask
   * anywhere to persist. ⭐ *`unrepresentable` beats *a promise to check*, on a credential path.*
   *
   * ⇒ The arm carries nothing. *"A value is stored — leave this empty to keep it, or type a new one to
   * replace it"* is said **in words**, by the caller's hint (from the producer's `has<Field>` boolean),
   * and the submit rule lives in the caller's `canSubmit`. This field's only job is `type="password"`
   * **plus `autoComplete="new-password"`** (spine **R-56** — see the render arm: an empty password
   * field with no `autoComplete` is a browser AUTOFILL target, and an autofilled value is
   * indistinguishable from a typed one, so it would defeat the omit-when-untouched rule at the one
   * seam this module cannot see), and never rendering a value the server did not send.
   *
   * ## ⛔ THE THREE-WAY VOCABULARY THIS ARM SITS IN (spine R-49 as refined by R-51)
   *
   * | word | producer | console renders | who can change it |
   * |---|---|---|---|
   * | `withheld` | the value is **never sent** (`secret: true` + `value: null`) | the `withheld` `CellAbsence` — an absence. ⛔ **NOT a field** | **nobody** |
   * | `unchangedSentinel` *(the SEMANTICS; ⛔ no sentinel VALUE exists)* | a stored value exists, signalled by `has<Field>` | **this arm**, empty, plus a sentence | the operator, by typing |
   * | `forbidden` | the read was refused | `ForbiddenState` naming the grant | a person, by granting it |
   *
   * ⛔ **THE RULE:** never a `secret` field for a `withheld` value, and never the `withheld` absence for a
   * sentinel-semantics one. A field implies you can write it; a withheld value has no field. The
   * inverse claims a value is unreachable when it is in fact editable.
   *
   * ⚠️ R-49's shared phrase, *"Masked by contract. The server never sends the secret value."*, is the
   * **`withheld`** row, i.e. the settings screen, **not** this arm: here the server does send *"a value
   * exists"*, just never the value.
   */
  | { readonly kind: 'secret' };

/** ⛔ The shipped behaviour, and the default. Named so the default is one value, not four literals. */
const TEXT_CONTROL: FormFieldControl = { kind: 'text' };

export interface FormFieldProps {
  /** ⛔ Wrapped in a `<label>`, so the association cannot be forgotten at a call site. */
  readonly label: string;
  /**
   * The control's `name`. ⚠️ Required, not optional — the same call the pre-auth field makes, and for
   * a weaker but real version of the same reason: browsers and password managers key on `name`, and a
   * nameless input is also un-findable in a form submission during debugging.
   */
  readonly name: string;
  readonly value: string;
  /** ⛔ The VALUE, not the event. No call site has a reason to reach into a DOM event. */
  readonly onChange: (value: string) => void;
  readonly testId: string;
  /**
   * ⛔ **REQUIRED, with no default.** An optional-with-a-default `required` gives every field whichever
   * default was chosen, silently — and the failure is invisible in every test and every screenshot.
   * The caller knows; making them say so costs one word.
   */
  readonly required: boolean;
  /** Permanent guidance under the control. ⚠️ Rendered whether or not there is a problem. */
  readonly hint?: string;
  /**
   * The current validation problem, or `null`/absent when the field is fine.
   *
   * ⛔ **A `state` block, so it is VISIBLE and announced** — never a hidden field or a silently
   * disabled submit. It sets `aria-invalid` AND joins `aria-describedby`, because a `role="alert"`
   * floating near an input is not associated with it: a screen-reader user tabbing to the field would
   * hear the label and nothing about why it was rejected.
   */
  readonly problem?: string | null;
  readonly placeholder?: string;
  /**
   * Render the value monospaced — for a field whose content is an **identifier**, not prose (a slug).
   *
   * ⚠️ Same prop, same reasoning, and the same scar as the deleted `FilterOption.mono`: a migration silently
   * dropped `font-mono` from pod names, no test noticed, and the only evidence a reviewer had was a
   * comment that was false.
   */
  readonly mono?: boolean;
  /**
   * Which control this field renders. ⛔ Defaults to `{ kind: 'text' }` — the shipped behaviour, so
   * every existing call site is byte-identical.
   */
  readonly control?: FormFieldControl;
  /**
   * Focus this control when it MOUNTS.
   *
   * ⛔ **For a field the operator's own action just revealed, and nothing else.** An inline form
   * opened by a button destroys the control that held focus, so without this the operator is dropped
   * on `document.body` and the field they asked for is reachable only by tabbing from the top of the
   * document. That is the case this exists for. ⚠️ It is NOT for a field present on arrival: focus
   * moved without a user action is disorienting, and on a page with more than one form it is a fight
   * over who wins.
   *
   * ⚠️ Declarative on purpose. React focuses the node at mount, so no call site needs a DOM handle
   * and this module exposes none — returning focus to a control that was already on screen is the
   * caller's imperative job (`focusControl` at the call site, `dialog-panel.tsx`'s `restoreFocus` in
   * the library).
   */
  readonly autoFocus?: boolean;
  /**
   * A key handler on the control — for a caller that submits on `Enter`.
   *
   * ⛔ The control only. This module renders no `<form>`, so `Enter` does nothing here by default,
   * and a single-field form where the operator's hands finish on `Enter` has to say what that key
   * does. ⚠️ It cannot reach `type`, `value`, `name` or the `aria` join: those are named props and
   * this is one handler, not a pass-through bag.
   */
  readonly onKeyDown?: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/**
 * The three ids + two attributes every arm of every field in this module wires identically.
 *
 * ⛔ **Hoisted out of the components rather than repeated in each**, because this join is the half
 * that gets forgotten: `aria-describedby` is BUILT from two independent inputs, and the failure mode
 * of getting it wrong (a dangling reference, or a present-but-empty `aria-describedby=""`) renders a
 * DOM in which every element is present and every text is right. One implementation ⇒ every arm
 * of {@link FormField} wires the join the same way.
 */
function describedBy(
  testId: string,
  hint: string | undefined,
  problem: string | null | undefined,
): {
  readonly hintId: string;
  readonly problemId: string;
  readonly ariaDescribedBy: string | undefined;
  readonly ariaInvalid: true | undefined;
} {
  const hintId = `${testId}-hint`;
  const problemId = `${testId}-problem`;
  /*
   * ⛔ BOTH ids, in reading order, and `undefined` rather than `''` when there is neither.
   * `aria-describedby=""` is a present-but-empty attribute: assistive technology treats it as a
   * broken reference rather than as no reference, and it renders in the DOM looking correct.
   */
  const described = [hint === undefined ? null : hintId, isBlank(problem) ? null : problemId]
    .filter((id) => id !== null)
    .join(' ');
  return {
    hintId,
    problemId,
    ariaDescribedBy: described === '' ? undefined : described,
    ariaInvalid: isBlank(problem) ? undefined : true,
  };
}

/** The hint line and the problem alert, in reading order. ⛔ One implementation for both fields. */
function FieldMessages({
  hint,
  problem,
  hintId,
  problemId,
}: {
  readonly hint: string | undefined;
  readonly problem: string | null | undefined;
  readonly hintId: string;
  readonly problemId: string;
}): React.JSX.Element {
  return (
    <>
      {hint === undefined ? null : (
        <span id={hintId} data-testid={hintId} className="max-w-[100ch] text-2xs text-muted">
          {hint}
        </span>
      )}
      {isBlank(problem) ? null : (
        <span
          id={problemId}
          data-testid={problemId}
          role="alert"
          /*
           * ⛔ `dataToneClass`, not a raw `text-state-*` literal. `caution` is the right verdict:
           * a validation problem is recoverable by the operator in the next keystroke, where `bad`
           * is reserved for a failure they cannot undo. ⚠️ The tone ratchet excludes `src/shell/`
           * precisely because the library is where tone is ENCODED — but going through the mapper
           * anyway is what keeps this field on the same scale as everything else.
           */
          className={`max-w-[100ch] text-2xs ${dataToneClass('caution', 'text')}`}
        >
          {problem}
        </span>
      )}
    </>
  );
}

/**
 * A labelled control with its hint and its problem, wired together for a screen reader.
 *
 * ⛔ **There is no `type` prop, and {@link FormFieldControl} is why that survived the expansion.** A
 * `type` this component forwarded blindly is how a credential field ends up as `type="text"`;
 * `AuthField` closes that with a union and this closes it with a closed arm set. ⚠️ Note the direction
 * the `secret` arm moved the guarantee: a password field now exists **here**, in the library, where
 * its `type="password"` is chosen by this file and cannot be overridden — rather than being
 * hand-rolled at the one call site that needs it, which is what "a password belongs to the pre-auth
 * surface" would have forced.
 */
export function FormField({
  label,
  name,
  value,
  onChange,
  testId,
  required,
  hint,
  problem,
  placeholder,
  mono,
  control,
  autoFocus,
  onKeyDown,
}: FormFieldProps): React.JSX.Element {
  const { hintId, problemId, ariaDescribedBy, ariaInvalid } = describedBy(testId, hint, problem);
  const kind = control ?? TEXT_CONTROL;

  /*
   * ⛔ ONE handler for the `<input>` and the `<textarea>` alike, typed on the UNION of the two
   * elements. Four per-arm lambdas would be four functions that must each be separately exercised to
   * reach 100%, and — worse — four places for *"emit the VALUE, not the event"* to be got wrong
   * independently.
   */
  function emit(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    onChange(event.target.value);
  }

  /* ⛔ Every arm carries the SAME treatment, name, validity wiring and test id. Only the ELEMENT and
     the arm's own constraints differ — which is the whole content of the union. */
  const shared = {
    name,
    value,
    required,
    placeholder,
    /* ⚠️ `undefined` on every existing call site, so React omits both attributes and nothing an
       adopter renders changes. See each prop for the one case it exists for. */
    autoFocus,
    onKeyDown,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
    onChange: emit,
    'data-testid': testId,
    className: mono === true ? `${FORM_FIELD_CLASS} font-mono` : FORM_FIELD_CLASS,
  };

  /*
   * ⛔ An exhaustive `switch` over the discriminant with **no `default`**. TypeScript proves every arm
   * returns, so a fifth member of {@link FORM_FIELD_CONTROL_KINDS} fails the BUILD rather than falling
   * into a silent fallback — and there is no unreachable default arm for coverage to be unable to
   * enter. (`form-field.test.tsx` iterates the constant, so the arm set and the switch cannot drift.)
   */
  function renderControl(): React.JSX.Element {
    switch (kind.kind) {
      case 'multiline':
        /* ⛔ A `<textarea>` is an EXPANSION of this component, never a sibling `FormTextArea`: a second
           component would duplicate the label / hint / problem / `aria-describedby` join above, and
           that join is the half that gets forgotten. */
        return <textarea rows={kind.rows} {...shared} />;
      case 'numeric':
        /* ⚠️ `inputMode` as well as `type` — `type="number"` alone still shows a full keyboard on
           several mobile browsers, and these are node counts typed on a laptop or a tablet alike. */
        return <input type="number" inputMode="numeric" min={kind.min} max={kind.max} {...shared} />;
      case 'secret':
        /*
         * ⛔⛔ **`autoComplete="new-password"`, AND IT IS THE SECOND HALF OF THE D-17 GUARANTEE
         * (spine R-56).** `type="password"` alone is a browser AUTOFILL AND SAVE TARGET, and that
         * reopens exactly the door the empty field was chosen to close:
         *
         *   Chrome autofills the operator's OWN saved password into the empty credential field
         *   → React fires `onChange` → the caller's touched-tracking marks the field dirty
         *   → `canSubmitForm` now includes the key → **the operator's personal password is persisted
         *     as the IdP client secret, 200 OK.**
         *
         * ⛔ Nothing in this module can distinguish that from a keystroke — the field is empty either
         * way — so *"an untouched field's key is OMITTED from the write body"* is satisfied and the
         * credential is still destroyed. ⇒ The defence has to be at the browser seam, and
         * `new-password` is the standard suppression: it declines the fill AND the save prompt.
         *
         * ⚠️ **`'off'` is NOT a substitute** — Chrome deliberately ignores `autocomplete="off"` on
         * password fields (its own documented anti-pattern handling), which is why the value is the
         * specific token and not the obvious one.
         *
         * ⛔ **On this arm ONLY.** A `text`/`multiline`/`numeric` field carrying it would suppress
         * legitimate autofill on ordinary form fields; `form-field.test.tsx` asserts presence here and
         * ABSENCE on the other three, so a refactor that hoists it into `shared` reds.
         */
        return <input type="password" autoComplete="new-password" {...shared} />;
      case 'url':
        /* ⛔ THE THREE ATTRIBUTES TRAVEL TOGETHER, and this file chooses all three — see the arm.
           `spellCheck={false}` because a pasted URL is not prose: a red underline under a webhook
           token reads as a defect in the value. */
        return <input type="url" autoComplete="off" spellCheck={false} {...shared} />;
      case 'text':
        return <input type="text" {...shared} />;
    }
  }

  return (
    <label className={FORM_LABEL_CLASS}>
      {label}
      {renderControl()}
      <FieldMessages hint={hint} problem={problem} hintId={hintId} problemId={problemId} />
    </label>
  );
}

/**
 * ⛔ `undefined`, `null` AND `''` all mean *"no problem"*.
 *
 * ⚠️ The `''` arm is not defensive padding: a caller computing a message by interpolation can produce
 * an empty string, and the outcome would be a **visible, empty `role="alert"`** plus an `aria-invalid`
 * input with nothing to say — the operator told that the field is wrong and given no words. That is
 * the exact defect `platform/errors.ts`'s `displayMessage` exists to close one layer down, and it is
 * cheaper to make unrepresentable here than to catch at every call site.
 */
function isBlank(problem: string | null | undefined): boolean {
  return problem === undefined || problem === null || problem.trim() === '';
}
